<template>
  <div class="expand diagnosis-case-card-content d-none d-md-flex">
    <vt-side-panel
      v-if="toggleSidepanel && caseData && caseData.requestLabel"
      :visible="toggleSidepanel"
    >
      <study-request-detail
        :cas-id="caseData.casId"
        :lab-id="labId"
      >
        <b-button
          id="requestsSidePanelClose"
          slot="buttons-end"
          v-shortkey="['shift', 'esc']"
          class="btn__icon btn__icon--primary pl-3 pr-0 py-0"
          @shortkey.prevent="closeRequestSidePanel()"
          @click="closeRequestSidePanel()"
        >
          <i
            class="fal fa-times"
            aria-hidden="true"
          />
        </b-button>
      </study-request-detail>
    </vt-side-panel>
    <div class="horizontal-divider m-0" />
    <div class="p-1 text-center">
      <vt-loading-button
        v-if="$_canPrintCaseReport()"
        qa="diagnosis-case-card-pathology-report-button"
        class="btn__link px-2 py-1"
        :params="{ promise: showAPReport, shortkeys: ['ctrl', 'shift', 'v'] }"
      >
        <div>
          <i
            class="far fa-file-contract mr-2"
            aria-hidden="true"
          />
          {{ $t('diagnosis.btns.report') }}
        </div>
      </vt-loading-button>
    </div>
    <div class="horizontal-divider m-0" />
    <div class="case-content">
      <div>
        <b-row class="people-section gr-card-info px-3 py-2">
          <b-col
            xl="12"
            class="info px-4"
            qa="diagnosis-case-card-pathologist-section"
          >
            <div>
              <small>{{ $t('diagnosis.labels.pathologist') }}
                <i
                  v-if="caseData.automaticPathologist"
                  v-tooltip="$tc('global.automaticAssignment')"
                  class="fal fa-magic ml-1"
                  aria-hidden="true"
                />
              </small>
            </div>
            <b-row>
              <b-col v-if="editingPathologist">
                <b-row>
                  <b-col
                    cols="9"
                    class="pr-0"
                  >
                    <vt-combo-select
                      v-model="pathologistSelected"
                      :disabled="disabledPathologistCombo"
                      class="nowrap"
                      :option-loader="getPersonnelPathologist"
                      label="fullName"
                    />
                  </b-col>
                  <b-col
                    cols="3"
                    class="d-flex p-0"
                  >
                    <b-button
                      :disabled="disabledPathologistCombo"
                      class="btn__icon btn__icon--primary ml-1 px-1"
                      @click="updateCasePathologist"
                    >
                      <i
                        class="far fa-check"
                        aria-hidden="true"
                      />
                    </b-button>
                    <b-button
                      class="btn__icon btn__icon--primary px-0"
                      @click="editingPathologist=false"
                    >
                      <i
                        class="far fa-times"
                        aria-hidden="true"
                      />
                    </b-button>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                v-else
                cols="auto"
              >
                <div>
                  <span
                    class="edition-box label-dark pathologist d-flex align-items-center"
                    :class="{ 'no-data': !caseData.pathologist }"
                  >
                    {{ caseData.pathologist ? caseData.pathologist.fullName : $t('diagnosis.labels.noPathologistAssigned') }}
                    <b-button
                      v-if="visiblePathologistReassignButton"
                      :disabled="disabledPathologistCombo"
                      class="edit-hover btn__icon btn__icon--primary px-0"
                      style="padding:0"
                      @click="onEditPathologist()"
                    >
                      <i
                        class="far fa-pen ml-2"
                        aria-hidden="true"
                      />
                    </b-button>
                  </span>
                </div>
              </b-col>
            </b-row>
          </b-col>
          <b-col
            xl="12"
            class="info px-4 mt-2"
            qa="diagnosis-case-card-resident-section"
          >
            <div>
              <small>{{ $t('diagnosis.labels.resident') }}
                <i
                  v-if="caseData.automaticResident"
                  v-tooltip="$tc('global.automaticAssignment')"
                  class="fal fa-magic ml-1"
                  aria-hidden="true"
                />
              </small>
            </div>
            <b-row>
              <b-col v-if="editingResident">
                <b-row>
                  <b-col
                    cols="9"
                    class="pr-0"
                  >
                    <vt-combo-select
                      v-model="residentSelected"
                      class="nowrap"
                      :option-loader="getPersonnelResident"
                      label="fullName"
                    />
                  </b-col>
                  <b-col
                    cols="3"
                    class="d-flex p-0"
                  >
                    <b-button
                      class="btn__icon btn__icon--primary ml-1 px-1"
                      @click="updateCaseResident"
                    >
                      <i
                        class="far fa-check"
                        aria-hidden="true"
                      />
                    </b-button>
                    <b-button
                      class="btn__icon btn__icon--primary px-0"
                      @click="editingResident=false"
                    >
                      <i
                        class="far fa-times"
                        aria-hidden="true"
                      />
                    </b-button>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                v-else
                cols="auto"
              >
                <div>
                  <span
                    class="edition-box label-dark pathologist d-flex align-items-center"
                    :class="{ 'no-data': !caseData.resident }"
                  >
                    {{ caseData.resident ? caseData.resident.fullName : $t('diagnosis.labels.noResidentAssigned') }}
                    <b-button
                      v-if="visibleResidentReassignButton"
                      :disabled="!$_isCaseEditable(caseData && caseData.status.code)"
                      class="edit-hover btn__icon btn__icon--primary px-0"
                      style="padding:0"
                      @click="onEditResident()"
                    >
                      <i
                        class="far fa-pen ml-2"
                        aria-hidden="true"
                      />
                    </b-button>
                  </span>
                </div>
              </b-col>
            </b-row>
          </b-col>
          <b-col
            xl="12"
            class="info px-4 mt-2"
          >
            <div>
              <small>{{ $t('global.priority') }}</small>
            </div>
            <b-row>
              <b-col v-if="editingPriority">
                <b-row>
                  <b-col
                    cols="9"
                    class="pr-0"
                  >
                    <vt-combo-select
                      v-model="prioritySelected"
                      :options="priorities"
                      label="name"
                    />
                  </b-col>
                  <b-col
                    cols="3"
                    class="d-flex p-0"
                  >
                    <b-button
                      :disabled="!prioritySelected"
                      class="btn__icon btn__icon--primary ml-1 px-1"
                      @click="updateCasePriority"
                    >
                      <i
                        class="far fa-check"
                        aria-hidden="true"
                      />
                    </b-button>
                    <b-button
                      class="btn__icon btn__icon--primary px-0"
                      @click="editingPriority=false"
                    >
                      <i
                        class="far fa-times"
                        aria-hidden="true"
                      />
                    </b-button>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                v-else
                cols="auto"
              >
                <div>
                  <span class="edition-box pathologist d-flex align-items-center">
                    <priority-dot
                      show-label
                      :priority="caseData.priority"
                    />
                    <b-button
                      v-if="visiblePriorityReassignButton"
                      :disabled="!$_isCaseEditable(caseData && caseData.status.code)"
                      class="edit-hover btn__icon btn__icon--primary px-0"
                      style="padding:0"
                      @click="onEditPriority()"
                    >
                      <i
                        class="far fa-pen ml-2"
                        aria-hidden="true"
                      />
                    </b-button>
                  </span>
                </div>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </div>
      <div class="horizontal-divider m-0" />
      <b-col>
        <b-form-row
          class="py-3"
        >
          <!-- Accounting Billable Units -->
          <template v-if="visibleAccountingBillableUnits">
            <b-col
              v-if="accountingUnits.length"
              cols="12"
            >
              <span class="ap-title ap-title__section">{{ $tc('global.billing') }}</span>
            </b-col>
            <b-col
              v-for="(accountingUnit) in accountingUnits"
              :key="accountingUnit.accountingUnit.id"
              md="6"
              lg="4"
              class="mb-md-3"
            >
              <div
                qa="diagnosis-case-card-accounting-modal-concepts-button"
                class="accounting-card component-bg--hover full-bordered d-flex flex-column text-center p-1"
                @click="showValuableConceptsModal(accountingUnit.accountingUnit.id)"
              >
                <small v-tooltip-auto-show="accountingUnit.accountingUnit.name">{{ accountingUnit.accountingUnit.symbol }}</small>
                <div class="ap-title ap-title__subtitle mr-1">
                  {{ accountingUnit.sumValue }}
                </div>
              </div>
            </b-col>
          </template>
          <!-- Service Activity Units -->
          <!-- TODO Front: Next b-col must be the same behaviour of Billing, add v-if to show only if serviceActivityUnit.length -->
          <b-col
            cols="12"
          >
            <span class="ap-title ap-title__section">{{ $tc('global.serviceActivity') }}</span>
          </b-col>
          <b-col
            md="6"
            lg="4"
            class="mb-md-3 mb-lg-0"
          >
            <div
              class="accounting-card component-bg--hover full-bordered d-flex flex-column text-center p-1"
              @click="showValueUnitModal"
            >
              <small v-tooltip-auto-show="$t('diagnosis.labels.valueUnit')">{{ $t('diagnosis.labels.valueUnit') }}</small>
              <div class="ap-title ap-title__subtitle mr-1">
                {{ caseData.urvTotalSum }}
              </div>
            </div>
          </b-col>
          <b-col
            md="6"
            lg="4"
            class="mb-md-3 mb-lg-0"
          >
            <div
              class="accounting-card component-bg--hover full-bordered d-flex flex-column text-center p-1"
              @click="showWorkUnitModal"
            >
              <small v-tooltip-auto-show="$t('diagnosis.labels.workUnit')">{{ $t('diagnosis.labels.workUnit') }}</small>
              <div class="ap-title ap-title__subtitle mr-1">
                {{ caseData.uclTotalSum }}
              </div>
            </div>
          </b-col>
        </b-form-row>
      </b-col>
      <div class="horizontal-divider m-0" />
      <div>
        <b-row class="px-3 pb-2 pt-3 text-center">
          <b-col cols="12">
            <b-container>
              <b-row>
                <b-col class="flex-column lh-0">
                  <span v-tooltip="`${consultationsResolvedCount} ${$tc('diagnosis.btns.of')} ${consultations.length} ${$tc('diagnosis.btns.completedConsultations')}`">
                    <b-button
                      :disabled="!visibleConsultations"
                      class="btn__icon btn__icon--primary"
                      @click="$emit('showConsultationModal')"
                    >
                      <i
                        class="far fa-comment-alt-dots"
                        aria-hidden="true"
                      />
                    </b-button>
                    <span>{{ consultationsResolvedCount }}/{{ consultations.length }}</span>
                  </span>
                </b-col>
                <b-col class="lh-0">
                  <b-button
                    v-tooltip="$t('diagnosis.notificationsModal.title')"
                    qa="diagnosis-case-card-diagnosis-notifications-button"
                    class="btn__icon btn__icon--primary"
                    @click="$emit('showCustomSentNotifications')"
                  >
                    <i
                      class="far fa-envelope"
                      aria-hidden="true"
                    />
                  </b-button>
                  <span qa="diagnosis-case-card-notifications-span">{{ customSentNotificationsSummary }}</span>
                </b-col>
                <!-- TODO: Analysis required -->
                <b-col
                  v-if="false"
                  class="d-flex flex-column lh-0"
                >
                  <b-button
                    v-tooltip="$t('diagnosis.btns.associatedCases')"
                    class="btn__icon btn__icon--primary"
                  >
                    <i
                      class="far fa-exchange-alt"
                      aria-hidden="true"
                    />
                  </b-button>
                  <span>0</span>
                </b-col>
              </b-row>
            </b-container>
          </b-col>
          <b-col
            v-if="caseData.caseOfReference || caseData.lastAmendmentDate"
            cols="12"
          >
            <div class="d-flex align-items-center mt-3">
              <b-badge
                v-if="caseData.caseOfReference"
                v-tooltip="$t('registry.btn.consultation')"
                class="ml-1 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
                pill
              >
                <span class="far fa-glasses" />
              </b-badge>
              <!-- TODO: Case shared indicator, uncomment when developed -->
              <b-badge
                v-if="false"
                v-tooltip="$t('diagnosis.modals.caseShared')"
                pill
                class="ml-1 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
              >
                <span class="far fa-share" />
              </b-badge>
              <!-- TODO: Report read by healthcare professional indicator, uncomment when developed -->
              <b-badge
                v-if="false"
                v-tooltip="$t('global.reportReadByHealthcareProfessional')"
                pill
                class="ml-1 is-cursor-pointer icon-badge indicator-bubble d-flex align-items-center justify-content-center"
              >
                <span class="far fa-stethoscope" />
              </b-badge>
              <b-badge
                v-if="caseData.lastAmendmentDate"
                v-tooltip="$t('global.amendedCase')"
                class="ml-1 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
                pill
              >
                <span class="far fa-money-check-edit" />
              </b-badge>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-row class="gr-card-info py-2 px-lg-4 px-xl-3">
        <b-col
          cols="12"
          class="d-flex align-items-center justify-content-between px-4 mt-1 mb-3"
        >
          <div class="d-flex align-items-center">
            <case-status-badge
              class="status-badge"
              :status="caseData.status"
              qa="diagnosis-case-card-status-badge"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
          class="info mb-3 px-4"
        >
          <div>
            <small>{{ $t('global.relevantClinicalData') }}</small>
          </div>
          <div>
            <span class="label-dark">{{ caseData.clinicalData ? caseData.clinicalData : '-' }}</span>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="info mb-3 px-4"
        >
          <div>
            <small>{{ $t('global.observations') }}</small>
          </div>
          <div>
            <span class="label-dark">{{ caseData.notes ? caseData.notes : '-' }}</span>
          </div>
        </b-col>
        <b-col
          v-if="caseData.requestLabel"
          class="info mb-3 px-4"
        >
          <div>
            <small>{{ $tc('global.request', 1) }}</small>
          </div>
          <div style="margin-top:-5px">
            <span class="label-dark">{{ caseData.requestLabel }}</span>
            <b-button
              v-tooltip="$t('diagnosis.btns.request')"
              qa="diagnosis-case-card-request-sidepanel-button"
              class="btn__icon btn__icon--primary px-2"
              @click="toggleRequestSidePanel()"
            >
              <i
                class="far fa-info-circle"
                aria-hidden="true"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-row class="px-4">
            <b-col cols="auto">
              <b-button
                v-b-toggle.collapse-3
                class="btn__link"
                @click="viewMore=!viewMore"
              >
                <div v-if="viewMore">
                  <i
                    class="mr-1 fal fa-plus"
                    aria-hidden="true"
                  />
                  {{ $t('diagnosis.btns.showMore') }}
                </div>
                <div v-else>
                  <i
                    class="mr-1 fal fa-minus"
                    aria-hidden="true"
                  />
                  {{ $t('diagnosis.btns.showLess') }}
                </div>
              </b-button>
            </b-col>
          </b-row>

          <b-collapse id="collapse-3">
            <b-row class="px-md-3 px-xl-4 mt-2">
              <b-col
                v-if="caseData.requestDate"
                xl="6"
                lg="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('grid.detail.casRequestDate') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">{{ caseData.requestDate | datetime }}</span>
                </div>
              </b-col>
              <b-col
                v-if="caseData.registrationDate"
                xl="6"
                lg="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('grid.detail.casRegistrationDate') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">{{ caseData.registrationDate | datetime }}</span>
                </div>
              </b-col>
              <b-col
                v-if="caseData.informedDate"
                xl="6"
                lg="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('grid.detail.casInformedDate') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">{{ caseData.informedDate | datetime }}</span>
                </div>
              </b-col>
              <b-col
                v-if="caseData.validationDate"
                xl="6"
                lg="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('grid.detail.casValidationDate') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">{{ caseData.validationDate | datetime }}</span>
                </div>
              </b-col>
              <div class="horizontal-divider m-0" />
            </b-row>
            <div class="px-md-3 px-xl-4">
              <div class="horizontal-divider m-0" />
            </div>
            <b-row class="px-md-3 px-xl-4 mt-2">
              <b-col
                xl="6"
                lg="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('diagnosis.labels.registrar') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">
                    {{ caseData.registerPersonnel ? caseData.registerPersonnel.fullName : '-' }}
                  </span>
                </div>
              </b-col>
              <b-col
                xl="6"
                lg="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('diagnosis.labels.administrative') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">{{ caseData.administrative ? caseData.administrative.fullName: '-' }}</span>
                </div>
              </b-col>
              <div class="horizontal-divider m-0" />
            </b-row>
            <b-row class="pt-2 px-md-3 px-xl-4">
              <div class="d-flex align-items-center col-auto">
                <span class="ap-title ap-title__divider">{{ $t('global.requester') }}</span>
              </div>
              <div class="col">
                <div class="horizontal-divider my-3" />
              </div>
            </b-row>
            <b-row class="px-md-3 px-xl-4 mt-2">
              <b-col
                cols="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('grid.detail.cenPhysName') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">
                    {{ caseData.physicianDepartment ? caseData.physicianDepartment.center.name : '-' }}
                  </span>
                </div>
              </b-col>
              <b-col
                cols="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('diagnosis.labels.unit') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">
                    {{ caseData.physicianDepartment ? caseData.physicianDepartment.name : '-' }}
                  </span>
                </div>
              </b-col>
              <b-col
                cols="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $tc('global.professional', 1) }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">
                    {{ caseData.physicianPetitioner ? caseData.physicianPetitioner.fullName : '-' }}
                  </span>
                </div>
              </b-col>
            </b-row>
            <b-row class="pt-2 px-md-3 px-xl-4">
              <div class="d-flex align-items-center col-auto">
                <span class="ap-title ap-title__divider">{{ $t('diagnosis.labels.destination') }}</span>
              </div>
              <div class="col">
                <div class="horizontal-divider my-3" />
              </div>
            </b-row>
            <b-row class="px-md-3 px-xl-4 mt-2">
              <b-col
                cols="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('grid.detail.cenPhysName') }}</small>
                </div>
                <div
                  class="collapse-data"
                >
                  <span class="label-dark">
                    {{ caseData.destinationDepartment ? caseData.destinationDepartment.center.name : '-' }}
                  </span>
                </div>
              </b-col>
              <b-col
                cols="12"
                class="info mb-3"
              >
                <div>
                  <small>{{ $t('diagnosis.labels.unit') }}</small>
                </div>
                <div
                  class="collapse-data"
                >
                  <span class="label-dark">
                    {{ caseData.destinationDepartment ? caseData.destinationDepartment.name : '-' }}
                  </span>
                </div>
              </b-col>
              <b-col
                cols="12"
                class="info"
              >
                <div>
                  <small>{{ $t('global.receiver') }}</small>
                </div>
                <div class="collapse-data">
                  <span class="label-dark">{{ caseData.casAttn ? caseData.casAttn : '-' }}</span>
                </div>
              </b-col>
            </b-row>
          </b-collapse>
        </b-col>
      </b-row>
    </div>
    <!-- Modals -->
    <share-case-modal
      ref="shareModal"
      :case-share="caseData"
    />
    <value-unit-modal
      ref="valueUnitModal"
      :laboratory-id="labId"
      :patient-id="caseData.patient.id"
      :case-id="caseId"
      :total-u-r-v="caseData.urvTotalSum"
    />
    <work-unit-modal
      ref="workUnitModal"
      :laboratory-id="labId"
      :case-id="caseId"
      :total-u-c-l="caseData.uclTotalSum"
    />
    <cost-modal ref="costModal" />
    <valuable-concepts-modal
      ref="valuableConceptsModal"
      @refresh="onRefreshAccountingUnits()"
    />
  </div>
</template>

<script>
import PriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot'
import CaseStatusBadge from '@/components/common/CommonCaseStatusBadge'
import ShareCaseModal from '@/modules/diagnosisModule/views/form/modals/ShareCaseModal'
import ValueUnitModal from '@/modules/diagnosisModule/views/form/modals/ValueUnitModal'
import WorkUnitModal from '@/modules/diagnosisModule/views/form/modals/WorkUnitModal'
import CostModal from '@/modules/diagnosisModule/views/form/modals/CostModal'
import sessionStore from '@/session/sessionStore'
import { personnelService } from '@/api/resources/personnelService'
import diagnosisCaseService from '@/modules/diagnosisModule/services/DiagnosisCaseService'
import { cloneDeep } from 'lodash'
import StudyRequestDetail from '@/components/common/detail/CommonRequestDetail'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { DiagnosisConsultationStatusEnum } from '@/modules/diagnosisModule/services/diagnosis-consultation/diagnosis-consultation.dto'
import DiagnosisCaseMixin from '@/modules/diagnosisModule/mixins/DiagnosisCaseMixin'
import { CommonResponsibilityCodeEnum } from '@/api/services/common/responsibility/common-responsibility.dto'
import ValuableConceptsModal from '@/components/valuable-concepts-modal/valuable-concepts-modal.vue'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { StoreUtils } from '@/shared/util/store-utils'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'

export default {
    name: 'DiagnosisCaseCard',
    components: {
        PriorityDot,
        CaseStatusBadge,
        ShareCaseModal,
        ValueUnitModal,
        WorkUnitModal,
        CostModal,
        StudyRequestDetail,
        ValuableConceptsModal
    },
    mixins: [
        CaseOperationGuardMixin,
        DiagnosisCaseMixin
    ],
    props: {
        showContent: Number,
        complementariesCount: Number,
        samplesCount: Number,
        labId: Number,
        customSentNotificationsSummary: Number
    },
    data() {
        return {
            pathologistSelected: null,
            residentSelected: null,
            prioritySelected: null,
            transcriptionSelected: null,
            editingPathologist: false,
            editingResident: false,
            editingPriority: false,
            editingTranscription: false,
            selected: null,
            optionsTranscription: [
                { label: this.$t('global.yes'), id: true },
                { label: this.$t('global.no'), id: false }
            ],
            toggleSidepanel: false,
            valueTitle: null,
            viewMore: true,
            accountingUnits: []
        }
    },
    computed: {
        priorities() {
            return sessionStore.state.priorities.filter(priority => priority.active)
        },
        hasURVActive() {
            const activeModule = StoreUtils.selectedLaboratoryConfiguration?.activeModules.find(
                module => module.moduleCode === 'configuration.parameter.laboratory.module.accountancy.urv.active')
            return !!activeModule?.activeStatus
        },
        hasUCLActive() {
            const activeModule = StoreUtils.selectedLaboratoryConfiguration?.activeModules.find(
                module => module.moduleCode === 'configuration.parameter.laboratory.module.accountancy.ucl.active')
            return !!activeModule?.activeStatus
        },
        visibleAccountingBillableUnits() {
            return ModuleOperationGuard.canAccessBilling && HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.BILLABLE_CONCEPTS_AND_CASES_ACCESS)
        },
        consultationsResolvedCount() {
            return this.consultations.filter(consultation => consultation.status.code === DiagnosisConsultationStatusEnum.SOLVED).length
        },
        visiblePathologistReassignButton() {
            return this.$_canEditCase() && !this.$_isCaseValidated(this.caseData?.status.code) && !this.$_isCaseInAmendment(this.caseData?.status.code)
        },
        visibleResidentReassignButton() {
            return this.$_canEditCase() && !this.$_isCaseValidated(this.caseData?.status.code)
        },
        visiblePriorityReassignButton() {
            return this.$_canEditCase() && !this.$_isCaseValidated(this.caseData?.status.code)
        },
        disabledPathologistCombo() {
            return !(!this.$_isCaseValidated(this.caseData.status.code) && !this.$_isCaseInAmendment(this.caseData.status.code))
        },
        isReadOnly() {
            return !this.$_canSaveCase(this.casId) ||
            (!!this.casId && !this.$_isCaseEditable(this.caseData?.status.code))
        }
    },
    async mounted() {
        await this.getAccountingUnits()
    },
    methods: {
        onEditPathologist() {
            this.pathologistSelected = this.caseData.pathologist
            this.editingPathologist = !this.editingPathologist
        },
        onEditResident() {
            this.residentSelected = this.caseData.resident
            this.editingResident = !this.editingResident
        },
        onEditPriority() {
            this.prioritySelected = this.caseData.priority
            this.editingPriority = !this.editingPriority
        },
        updateCasePathologist() {
            const pathologistPerId = this.pathologistSelected ? this.pathologistSelected.id : null
            this.updateCase({ pathologistPerId: pathologistPerId })
            this.editingPathologist = false
        },
        updateCaseResident() {
            const residentPerId = this.residentSelected ? this.residentSelected.id : null
            this.updateCase({ residentPerId: residentPerId })
            this.editingResident = false
        },
        updateCasePriority() {
            const priorityId = this.prioritySelected ? this.prioritySelected.id : null
            this.updateCase({ priorityId: priorityId })
            this.editingPriority = false
        },
        async updateCase(data) {
            this.updateLoading(true)
            const params = cloneDeep(data)
            params.originalConcurrencyUpdatedAt = this.caseData.originalConcurrencyUpdatedAt
            const [err] = await this.toCall(diagnosisCaseService.updateCase(this.labId, this.caseData.casId, params))
            this.updateLoading(false)
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$tc('global.case', 1), err, () => {
                        this.caseData.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.updateCase(data)
                    })
                } else {
                    this.$_showErrorModalFromErrorResponse(err)
                    return false
                }
            }
            this.showToastSuccessGlobal(this.$t('diagnosis.caseUpdated'))
            this.$emit('updateCase')
            return true
        },
        getPersonnelPathologist() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.labId, CommonResponsibilityCodeEnum.PATHOLOGIST)
        },
        getPersonnelResident() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.labId, CommonResponsibilityCodeEnum.RESIDENT)
        },
        toggleRequestSidePanel() {
            this.toggleSidepanel = !this.toggleSidepanel
        },
        closeRequestSidePanel() {
            this.toggleSidepanel = false
        },
        showValueUnitModal() {
            this.$refs.valueUnitModal.show()
        },
        showWorkUnitModal() {
            this.$refs.workUnitModal.show()
        },
        showCostModal() {
            this.$refs.costModal.show()
        },
        showValuableConceptsModal(accountingId) {
            if (this.visibleAccountingBillableUnits) {
                this.$refs.valuableConceptsModal.show({ isBillingModule: false, caseId: this.caseId, accountingId, caseState: this.caseData.status.code })
            }
        },
        async onRefreshAccountingUnits() {
            await this.getAccountingUnits()
            this.$emit('reload-info-concepts')
        },
        async getAccountingUnits() {
            if (this.visibleAccountingBillableUnits) {
                const accountingUnits = await this.$_toCallSecure(billingAppliedBillableConceptService.getSumPerAccountingUnit(this.labId, this.caseId))
                this.accountingUnits = accountingUnits ?? []
            }
        },
        async showAPReport() {
            const getCaseReportPDFParams = {
                laboratoryId: this.labId,
                caseId: this.caseId,
                caseLabel: this.caseNumber,
                print: false
            }
            await HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
        }
    }
}
</script>

<style lang="scss" scoped>

.accounting-card {
    cursor: pointer;
}

.col-btn span{
  color: vt-color(primary);
  transition: .2s;
  font-size: font-size(normal);
}

.col-btn:hover span{
  color: vt-color(dark-text);
}

.switch {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 17px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  transition: all 0.3s;
  cursor: pointer;
}

.switch::after {
  content: "";
  position: absolute;
  width: 15px;
  height: 15px;
  border-radius: 18px;
  background-color: white;
  top: 1px;
  left: 1px;
  transition: all 0.3s;
}

input[type="checkbox"]:checked + .switch::after {
  transform: translateX(15px);
}

input[type="checkbox"]:checked + .switch {
  background-color: vt-color(primary);
}

.offscreen {
  position: absolute;
  left: -9999px;
}

.main-section-btn {
  width: 100%;
  background: vt-color(white-light);
  border-radius: 0;
  border: transparent;
  border-left: 1px solid vt-color(base-text, 0.8);
  color: vt-color(primary);
  transition: 0.3s;
  position: relative;
}

.main-section-btn.first {
  border-left: transparent;
}

.main-section-btn:hover {
  background: vt-color(light-primary);
}

.main-section-btn.btn-active {
  background: vt-color(primary);
  color: vt-color(white-light);
}

.main-section-btn .btn-badge {
  position: absolute;
  top: 10px;
  right: -12px;
}

.main-section-btn .btn-badge .vt-nav-badge {
  left: 0;
  bottom: 0;
}

.edition-box {
  height: 18px;
}

.edition-box.no-data {
  color: vt-color(base-text);
}

.pathologist:hover .edit-hover {
  display: block;
}

.edit-hover {
  display: none;
}

.case-header {
  padding-bottom: 12px;
}

.case-header span {
  font-size: font-size(very-normal);
}

.pop-case {
  padding-left: 0;
  padding-bottom: 0;
  margin-bottom: 0;
  width: 200px;
}

.pop-case li {
  list-style: none !important;
  padding: 10px !important;
  border-bottom: 1px solid rgb(218, 218, 218);
}

.view-more {
  color: vt-color(primary);
  transition: 0.2s;
  cursor: pointer;
}

.view-more:hover {
  color: vt-color(dark-primary);
}

.mid-section-title {
  color: vt-color(primary);
}

.case-content .info span {
  line-height: 20px;
  font-size: font-size(very-small);
}

.case-content .info {
  text-align: justify;
}

.diagnosis-case-card-content .btn {
  font-size: font-size(big);
}

.diagnosis-case-card-content .case-btns-min {
  display: none !important;
}

.diagnosis-case-card-content .people-section .truncate {
  width: 130px;
  height: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1300px) {
  .col-btn span{
    color: vt-color(primary);
    transition: .2s;
    font-size: font-size(very-small);
  }

  .col-btn:hover span{
    color: vt-color(dark-text);
  }

  .case-header span {
    font-size: font-size(very-small);
  }

  .diagnosis-case-card-content .case-btns-min {
    display: block !important;
  }

  .diagnosis-case-card-content .main-btns .btn {
    font-size: font-size(normal);
  }

  .diagnosis-case-card-content .btn {
    font-size: font-size(normal);
  }

  .case-content .info span {
    line-height: 20px;
    font-size: font-size(small);
  }

  .case-content .gr-card-info {
    padding-left: 7px !important;
    padding-right: 7px !important;
  }
}

/*--Sizes for laptop screen--*/
@media only screen and (max-width: 1366px) and (min-width: 1360px) {
  .case-header span {
    font-size: font-size(very-small);
  }

  .diagnosis-case-card-content .expand .case-alerts {
    display: none !important;
  }

  .diagnosis-case-card-content .case-btns-min {
    display: block !important;
  }

  .diagnosis-case-card-content .main-btns {
    display: none !important;
  }

  .diagnosis-case-card-content .people-section .info {
    margin-bottom: 10px;
    flex: 0 0 100%;
    max-width: 100%;
  }

  .diagnosis-case-card-content .people-section .info span {
    font-size: font-size(very-small);
  }

  .diagnosis-case-card-content .people-section .truncate {
    width: 170px;
    height: 15px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .case-content .info span {
    line-height: 20px;
    font-size: font-size(small);
  }

  .case-content .gr-card-info {
    padding-left: 7px !important;
    padding-right: 7px !important;
  }
}
</style>
